import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { PageShell } from '../components/layout/PageShell';
import { StatusIcon, StatusIconVariant } from '../components/layout/StatusIcon';
// @mcc-dev-preview:import
import { resolveDevState } from '../dev/devPreview';
import './CheckingVerificationResults.css';

// @mcc-dev-preview:start dev-preview-types
type CheckVerificationDevState = 'pending' | 'approved' | 'rejected' | 'timeout';

const CHECK_VERIFICATION_DEV_STATES: CheckVerificationDevState[] = [
  'pending',
  'approved',
  'rejected',
  'timeout',
];
// @mcc-dev-preview:end dev-preview-types

interface VerificationStatus {
  status: 'pending' | 'approved' | 'rejected' | 'timeout';
  message: string;
  progress: number;
}

const CheckVerificationResults: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const devState = resolveDevState(searchParams, CHECK_VERIFICATION_DEV_STATES, [
    {
      when: (p) => p.get('stayPending') === 'true',
      state: 'pending',
    },
  ]);
  const cardId = searchParams.get('cardId');
  const stayPending = devState === 'pending' || searchParams.get('stayPending') === 'true';

  const [verificationStatus, setVerificationStatus] = useState<VerificationStatus>({
    status: 'pending',
    message: 'Checking verification results...',
    progress: 0
  });

  const [pollCount, setPollCount] = useState(0);
  const [isPolling, setIsPolling] = useState(true);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const startTimeRef = useRef<number>(Date.now());
  const pollCountRef = useRef<number>(0);

  const checkVerificationStatus = async (currentPollCount: number): Promise<{ status: string; message: string }> => {
    await new Promise(resolve => setTimeout(resolve, 1000));

    if (stayPending) {
      return { status: 'pending', message: 'Verification still in progress...' };
    }

    if (currentPollCount < 3) {
      return { status: 'pending', message: 'Verification still in progress...' };
    }
    if (currentPollCount === 3) {
      return { status: 'approved', message: 'Verification approved!' };
    }
    return { status: 'rejected', message: 'Verification rejected' };
  };

  const pollVerification = async () => {
    if (!isPolling) return;

    try {
      const elapsed = Date.now() - startTimeRef.current;
      const timeoutDuration = 60000;

      if (!stayPending && elapsed >= timeoutDuration) {
        setVerificationStatus({
          status: 'timeout',
          message: 'Verification check timed out. Please try again.',
          progress: 100
        });
        setIsPolling(false);
        if (intervalRef.current) clearInterval(intervalRef.current);
        return;
      }

      const result = await checkVerificationStatus(pollCountRef.current);
      pollCountRef.current += 1;
      setPollCount(pollCountRef.current);

      const progress = Math.min(100, (pollCountRef.current / 5) * 100);

      if (result.status === 'approved') {
        setVerificationStatus({ status: 'approved', message: result.message, progress: 100 });
        setIsPolling(false);
        if (intervalRef.current) clearInterval(intervalRef.current);
        setTimeout(() => {
          if (cardId) navigate(`/purchase-crypto?cardId=${cardId}`);
        }, 2000);
      } else if (result.status === 'rejected') {
        setVerificationStatus({ status: 'rejected', message: result.message, progress: 100 });
        setIsPolling(false);
        if (intervalRef.current) clearInterval(intervalRef.current);
        setTimeout(() => {
          navigate(`/error-page?cardId=${cardId}&error=Verification rejected`);
        }, 2000);
      } else {
        setVerificationStatus({ status: 'pending', message: result.message, progress });
      }
    } catch (error) {
      console.error('Error checking verification status:', error);
      setVerificationStatus({
        status: 'timeout',
        message: 'Error checking verification status. Please try again.',
        progress: 100
      });
      setIsPolling(false);
      if (intervalRef.current) clearInterval(intervalRef.current);
    }
  };

  // @mcc-dev-preview:start dev-preview-seed
  useEffect(() => {
    if (!devState) return;

    setIsPolling(false);

    switch (devState) {
      case 'pending':
        setVerificationStatus({
          status: 'pending',
          message: 'Verification still in progress...',
          progress: 40,
        });
        break;
      case 'approved':
        setVerificationStatus({
          status: 'approved',
          message: 'Verification approved!',
          progress: 100,
        });
        break;
      case 'rejected':
        setVerificationStatus({
          status: 'rejected',
          message: 'Verification rejected',
          progress: 100,
        });
        break;
      case 'timeout':
        setVerificationStatus({
          status: 'timeout',
          message: 'Verification check timed out. Please try again.',
          progress: 100,
        });
        break;
      default:
        break;
    }
  }, [devState]);
  // @mcc-dev-preview:end dev-preview-seed

  useEffect(() => {
    if (devState) return;

    if (!cardId) {
      navigate('/error-page?error=No card ID provided');
      return;
    }

    pollCountRef.current = 0;
    setPollCount(0);
    pollVerification();

    intervalRef.current = setInterval(() => {
      if (isPolling) pollVerification();
    }, 5000);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      setIsPolling(false);
    };
  }, [cardId, navigate, devState]);

  const iconVariant: StatusIconVariant =
    verificationStatus.status === 'approved'
      ? 'success'
      : verificationStatus.status === 'rejected'
        ? 'error'
        : 'loading';

  const progressColor =
    verificationStatus.status === 'approved'
      ? 'var(--ev-status-success)'
      : verificationStatus.status === 'rejected'
        ? 'var(--ev-status-error)'
        : 'var(--ev-btn-primary)';

  return (
    <PageShell showFooter={false} wide>
      <h1 className="ev-display-headline ev-animate-in">Checking your ID</h1>
      <p className="ev-display-subtitle ev-animate-in">Please wait, won&apos;t be long</p>

      <div className="ev-shell-card cvr-card ev-animate-in">
        <StatusIcon variant={iconVariant} />
        <p className="ev-status-body">{verificationStatus.message}</p>

        <div className="cvr-progress">
          <div className="cvr-progress-track">
            <div
              className="cvr-progress-fill"
              style={{ width: `${verificationStatus.progress}%`, backgroundColor: progressColor }}
            />
          </div>
          <span className="cvr-progress-label">{Math.round(verificationStatus.progress)}% complete</span>
        </div>

        {verificationStatus.status === 'timeout' && (
          <button type="button" className="ev-btn-primary" onClick={() => window.location.reload()}>
            Retry
          </button>
        )}
      </div>
    </PageShell>
  );
};

export default CheckVerificationResults;
